import {
  ChangeDetectionStrategy,
  Component,
  contentChild,
  contentChildren,
  effect,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { NzTableBridgeComponent } from '../../bridges/nz-table/nz-table-bridge.component';
import { GridCommandManager } from '../../core/commands/grid-command-manager';
import { GridEventBus } from '../../core/event-bus/grid-event-bus';
import { GridFacade } from '../../core/facade/grid.facade';
import type { GridBridge } from '../../core/interfaces/grid-bridge.interface';
import type { CompanyColumnDef } from '../../core/interfaces/grid-column.types';
import type { GridConfig } from '../../core/interfaces/grid-config.types';
import { DEFAULT_GRID_CONFIG } from '../../core/interfaces/grid-config.types';
import { GridPluginManager } from '../../core/plugin-manager/grid-plugin-manager';
import { GridStateStore } from '../../core/state/grid-state.store';
import { GRID_PLUGIN } from '../../core/tokens/grid-plugin.token';
import type { GridPlugin } from '../../core/interfaces/grid-context.types';
import { CompanyColumnComponent } from '../../directives/column.component';
import { CompanyExpandDefDirective } from '../../directives/expand-def.directive';

export interface CompanyGridStateMetrics {
  readonly totalRecords: number;
  readonly selectedCount: number;
  readonly currentPage: number;
}

function shallowEqualMetrics(a: CompanyGridStateMetrics, b: CompanyGridStateMetrics): boolean {
  return (
    a.totalRecords === b.totalRecords &&
    a.selectedCount === b.selectedCount &&
    a.currentPage === b.currentPage
  );
}

function shallowEqualConfig(a: Partial<GridConfig>, b: Partial<GridConfig>): boolean {
  if (a === b) return true;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof GridConfig>;
  return [...keys].every(key => a[key] === b[key]);
}

/**
 * Host da biblioteca @enacom/data-grid usando `nz-table` como motor de renderização.
 *
 * Suporta as seguintes capacidades via `[config]`:
 * - `frontPagination` — paginação/sort/filtro internos (modo cliente)
 * - `bordered`, `size`, `title`, `footer`, `ellipsis`, `scrollY`
 * - `expandable` — ativa linhas expansíveis (requer `<ng-template companyExpandDef>`)
 * - `paginationType`, `paginationPosition`, `simplePagination`
 *
 * Plugins disponíveis (inseridos como filhos):
 * - `<company-grid-selection-plugin>`
 * - `<company-grid-filter-plugin>`
 * - `<company-grid-column-plugin>`
 * - `<company-grid-export-plugin>`
 */
@Component({
  selector: 'company-nztable-data-grid',
  exportAs: 'companyNzTableDataGrid',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NzTableBridgeComponent],
  providers: [GridStateStore, GridEventBus, GridCommandManager, GridPluginManager, GridFacade],
  templateUrl: './nz-table-data-grid.component.html',
  styleUrl: './nz-table-data-grid.component.scss',
})
export class CompanyNzTableDataGridComponent<T = unknown> {
  readonly data = input<readonly T[]>([]);
  readonly loading = input(false);
  readonly config = input<Partial<GridConfig>>({});

  readonly stateChange = output<CompanyGridStateMetrics>();

  readonly store = inject(GridStateStore<T>);
  readonly facade = inject(GridFacade<T>);
  private readonly eventBus = inject(GridEventBus);
  private readonly commands = inject(GridCommandManager);
  private readonly pluginManager = inject(GridPluginManager);

  private readonly bridgeRef = viewChild(NzTableBridgeComponent);
  private readonly columnComponents = contentChildren(CompanyColumnComponent);
  private readonly expandDef = contentChild(CompanyExpandDefDirective);
  private readonly plugins = contentChildren(GRID_PLUGIN);
  private readonly attachedPlugins = new WeakSet<GridPlugin>();

  constructor() {
    effect(() => this.store.setRowData(this.data()));
    effect(() => this.store.setLoading(this.loading()));

    let lastAppliedConfig: GridConfig | null = null;
    effect(() => {
      const merged = { ...DEFAULT_GRID_CONFIG, ...this.config() };
      if (lastAppliedConfig && shallowEqualConfig(lastAppliedConfig, merged)) return;
      lastAppliedConfig = merged;
      this.applyConfig(merged);
    });

    effect(() => {
      const bridge = this.bridgeRef();
      let columns: readonly CompanyColumnDef<T>[];
      try {
        columns = this.columnComponents().map(col => col.columnDef());
      } catch {
        return;
      }
      this.store.setColumns(columns);
      bridge?.setColumns(columns);
    });

    effect(() => this.bridgeRef()?.setRowData(this.store.filteredData()));
    effect(() => this.bridgeRef()?.setLoading(this.store.loading()));

    effect(() => {
      const bridge = this.bridgeRef();
      if (!bridge) return;
      this.facade.bindBridge(bridge);
    });

    effect(() => {
      const bridge = this.bridgeRef();
      if (!bridge) return;

      for (const plugin of this.plugins()) {
        if (this.attachedPlugins.has(plugin)) continue;
        plugin.attach({
          store: this.store as unknown as GridStateStore<unknown>,
          bridge: bridge as unknown as GridBridge<unknown>,
          eventBus: this.eventBus,
          commands: this.commands,
        });
        this.pluginManager.register(plugin);
        this.attachedPlugins.add(plugin);
      }
    });

    // Atualiza o template de expansão sempre que `companyExpandDef` mudar.
    effect(() => {
      const bridge = this.bridgeRef();
      if (!bridge) return;
      bridge.setExpandTemplate(this.expandDef()?.template ?? null);
    });

    let lastEmittedMetrics: CompanyGridStateMetrics | null = null;
    effect(() => {
      const metrics: CompanyGridStateMetrics = {
        totalRecords: this.store.totalRecords(),
        selectedCount: this.store.selectedRowKeys().size,
        currentPage: this.store.currentPage(),
      };
      if (lastEmittedMetrics && shallowEqualMetrics(lastEmittedMetrics, metrics)) return;
      lastEmittedMetrics = metrics;
      this.stateChange.emit(metrics);
    });

    this.eventBus.on('CELL_VALUE_CHANGED', event => {
      const { rowIndex, column, previousValue, newValue, row } = event;
      this.commands.execute({
        label: `Editar ${column.field}`,
        execute: () => { this.store.updateCellValue(rowIndex, column.field, newValue); },
        undo: () => { this.store.updateCellValue(rowIndex, column.field, previousValue); },
      });
      const col = this.columnComponents().find(c => c.field() === column.field);
      col?.cellSave.emit({ row, field: column.field, previousValue, newValue });
    });

    this.eventBus.on('SELECTION_CHANGED', event =>
      this.store.setSelectedRows(event.selectedRows as T[]),
    );
  }

  private applyConfig(merged: GridConfig): void {
    this.store.setSelectionMode(merged.selectionMode);
    this.store.setPageSize(merged.pageSize);
    this.bridgeRef()?.configure(merged);
  }
}
